import type { TODO } from '../util/types';
import { Refetch } from './abstract/refetch';

interface IBasicUserInfo {
  displayName: string;
  displayPrefix: string;
  displaySuffix: string;
  emailAddress: string;
  firstName: string;
  userId: number;
  jobTitle: string;
  languageId: string;
  lastName: string;
  middleName: string;
  portraitUrl: string;
  prefixId: number;
  suffixId: number;
  timezoneId: string;
}

export interface IUserProfile {
  acceptedTermsOfService: boolean;
  contactData: TODO[];
  basicUserInfo: IBasicUserInfo;
  displayTotalRequiredActionsCount: number;
  displayTotalUnreadItemsCount: number;
  missingInviteAsNewUser: boolean;
  verifiedEmail: boolean;
  lastLoginDateTimeMillis: number;
}

export class UserProfile implements IUserProfile {
  acceptedTermsOfService: boolean;
  contactData: TODO[];
  basicUserInfo: IBasicUserInfo;
  displayTotalRequiredActionsCount: number;
  displayTotalUnreadItemsCount: number;
  missingInviteAsNewUser: boolean;
  verifiedEmail: boolean;
  lastLoginDateTimeMillis: number;

  displayName: string;
  displayPrefix: string;
  displaySuffix: string;
  emailAddress: string;
  firstName: string;
  userId: number;
  jobTitle: string;
  languageId: string;
  lastName: string;
  middleName: string;
  portraitUrl: string;
  prefixId: number;
  suffixId: number;
  timezoneId: string;

  constructor(data: IUserProfile) {
    this.acceptedTermsOfService = data.acceptedTermsOfService;
    this.contactData = data.contactData;
    this.basicUserInfo = data.basicUserInfo;
    this.displayTotalRequiredActionsCount =
      data.displayTotalRequiredActionsCount;
    this.displayTotalUnreadItemsCount = data.displayTotalUnreadItemsCount;
    this.missingInviteAsNewUser = data.missingInviteAsNewUser;
    this.verifiedEmail = data.verifiedEmail;
    this.lastLoginDateTimeMillis = data.lastLoginDateTimeMillis;

    this.displayName = data.basicUserInfo.displayName;
    this.displayPrefix = data.basicUserInfo.displayPrefix;
    this.displaySuffix = data.basicUserInfo.displaySuffix;
    this.emailAddress = data.basicUserInfo.emailAddress;
    this.firstName = data.basicUserInfo.firstName;
    this.userId = data.basicUserInfo.userId;
    this.jobTitle = data.basicUserInfo.jobTitle;
    this.languageId = data.basicUserInfo.languageId;
    this.lastName = data.basicUserInfo.lastName;
    this.middleName = data.basicUserInfo.middleName;
    this.portraitUrl = data.basicUserInfo.portraitUrl;
    this.prefixId = data.basicUserInfo.prefixId;
    this.suffixId = data.basicUserInfo.suffixId;
    this.timezoneId = data.basicUserInfo.timezoneId;
  }
}

class MembershipsByStatus {
  CURRENT: TODO[];
  PAST: TODO[];
  INVITED: TODO[];

  constructor(params: Partial<MembershipsByStatus> = {}) {
    this.CURRENT = params.CURRENT ?? [];
    this.PAST = params.PAST ?? [];
    this.INVITED = params.INVITED ?? [];
  }
}

export interface IMemberDetail {
  channelMemberId: string;
  activeMember: boolean;
  channelMemberDetailUrl: string;
  membershipsByStatus: MembershipsByStatus;
  rolesByStatus: string[];
  userProfile: IUserProfile;
  actions?: Record<string, object>;
}

class MemberDetail extends Refetch implements IMemberDetail {
  channelMemberId: string;
  activeMember: boolean;
  channelMemberDetailUrl: string;
  membershipsByStatus: MembershipsByStatus;
  rolesByStatus: string[];
  userProfile: UserProfile;
  actions: Record<string, object>;

  constructor(data: IMemberDetail) {
    super();
    this.channelMemberId = data.channelMemberId;
    this.activeMember = data.activeMember;
    this.channelMemberDetailUrl = data.channelMemberDetailUrl;
    this.membershipsByStatus = new MembershipsByStatus(
      data.membershipsByStatus
    );
    this.rolesByStatus = data.rolesByStatus;
    this.userProfile = new UserProfile(data.userProfile);
    this.actions = data.actions ?? {};
  }

  get _refetchUrl(): string {
    return 'channels/memberDetail';
  }

  get _refetchParams(): { channelId: string; memberId: string } {
    const character = this.channelMemberId.indexOf('-');
    const id = this.channelMemberId.substring(0, character);
    return {
      channelId: id,
      memberId: this.channelMemberId,
    };
  }
}

export default MemberDetail;
